/* Dynamic Plot code copyright 2021 (c) Tuva Labs Inc. */
/**
 * Measures-of-spread plot elements:
 *  Standard Error -- controller object for the Standard Error plot widget.
 *  Confidence Interval -- controller object for the Confidence Interval plot widget
 *
 *  Object Inheritance:
 *      Dyn.PlotElement -- base class for objects that control some part of the plot
 *          Dyn.AverageElementBase -- base class for average and spread controllers (e.g. Dyn.MeanElement, Dyn.StDeviationElement)
 *              Dyn.StdErrorElement -- class that creates Standard Error lines, one object with lines per plot.
 *
 *  Each object constructed by Dyn.StdErrorElement() controls all visible Std. Error lines for a single plot.
 * Added to Dyn.PlotShaper's list of plot elements, to make a plot.
 */
import AverageElementBase from "./averageElement";
import ContextMenuUtil from "../../../utils/ContextMenuUtil";

export const wrapDynSpreadElement = function(Dyn) {

    /** abstract base class for all measures-of-spread in plot,
     * like StDeviationElement, InterQuartRangeElement, etc.
     *  -StDeviationElement extends SpreadElement to calculate and draw StDev
     *      -SpreadElement extends AverageElementBase to calculate and draw various ranges
     *          -AverageElementBase extends PlotElement to calculate and draw line or range stats.
     *              -PlotElement is the base class for all elements added to and removed from plot
     */
    class SpreadElement extends AverageElementBase {

        constructor( Dyn, priority, controlAndReplace ) {
            super( Dyn, priority, controlAndReplace );
        }

        /**
         * {OVERRIDE} used when computeOneAverageAndLine() passes an array rather than a scalar value,
         * so that a region is displayed on the axis, rather than a single line on the axis.
         * @returns {boolean}
         */
        get isRegion() {
            return true;
        }

        static unitTest() {
            let unitTester = new Dyn.UnitTester( "Dyn.SpreadElement.unitTest()" );
            function assertEqual( a, b, toDecimalPlaces ) {
                a=Dyn.mathUtil.roundToDecimalPlaces(a,toDecimalPlaces);
                b=Dyn.mathUtil.roundToDecimalPlaces(b,toDecimalPlaces);
                unitTester.test( a === b, "SpreadElement.unitTest() failure, "+a+" !== "+b  );
            }

            // Test Standard Error computation
            // in R: options(digits=22); d <- c(600, 470, 170, 430, 300); sd(d)/sqrt(length(d))
            //      = 73.66138744281157357818
            let stErrorExample = [ 600, 470, 170, 430, 300 ];
            assertEqual( Dyn.mathUtil.stdErrorOfNumericArray( stErrorExample ), 73.66138744281157357818, 14 );

            // Test Mean Average Deviation computation
            //  simple example from https://www.indeed.com/career-advice/career-development/how-to-calculate-average-deviation
            let madExample = [23, 30, 31, 15, 46],
                madMean = Dyn.mathUtil.meanOfNumericArray( madExample );
            assertEqual( Dyn.mathUtil.meanAvDevOfNumericArray( madExample ), 8.0, 14 );
            assertEqual( Dyn.mathUtil.meanAvDevOfNumericArray( madExample, madMean ), 8.0, 14 );

            unitTester.report();
            return true;
        }
    }
    // unit test run during debugging only
    Dyn.debugUnitTestHasRun_SpreadElement = ( Dyn.debugging ? SpreadElement.unitTest() : false );

    //-----------------------------------------------------------------------------------

    /**
     * Dyn.StDeviationElement()
     */
    class StDeviationElement extends SpreadElement {

        constructor( Dyn ) {
            super( Dyn,
                Dyn.EPlotPriority.kPriorityStDeviation,
                Dyn.EPlotControl.kControlsStDeviation );
            this.numStDevs = 1; // integer, default 1, max 3 according to control panel setting
        }

        get kUndoAction() {
            return 'Standard Deviation';
        }

        get kStatName() {
            return {
                notSampling: "Std. Deviation",
                viewSample: "Sample Std. Deviation (s)",
                viewPopulation: "Population Std. Deviation (𝜎)",
                viewDistribution: "Std. Deviation of Distribution",
                nameOfDistrAttr: "Sample Std. Deviations"
            };
        }

        save() {
            let plotElementModel = super.save();
            plotElementModel.numStDevs = this.numStDevs;
            return plotElementModel;
        }

        restore( savedData ) {
            super.restore( savedData );
            if( savedData.numStDevs ) {
                this.numStDevs = savedData.numStDevs;
            }
        }

        /** Update adornments (specific to this element) to show +/-1 standard deviation lines on the plot area */
        calcAdornments( ioAdornArray, iconPositions, xAxisView, yAxisView, noAnimation ) {
            var plotCells = this.plotShaper.bins.cells,
                view = new Dyn.AverageViewUpdater( this, ioAdornArray, xAxisView, yAxisView, noAnimation );
            //view.lineColor = Dyn.plotUtil.stDevColor;
            view.nameOfAverage = Dyn.loc( this.kStatName[this.getSamplingModeKey()] );
            view.nameOfSampleAttr = Dyn.loc( this.kStatName.viewSample );
            view.nameOfDistrAttr = Dyn.loc( this.kStatName.nameOfDistrAttr );
            view.setColor( Dyn.plotUtil.stDevColor );

            // make the St. Dev. button as the 1st adornment, if needed
            if( view.adornments.length === 0 ) {
                view.adornments.push( this.makeMenuButton() );
                this.selectedAdornment = 1; // default to showing button at 1 SD
            }
            view.numAdornmentsUpdated++;

            // compute the StDevation of any numeric attributes on X and Y axis, for each cell created by the categorical axes.
            for( let i = 0, j = plotCells.length; i < j; i++ ) {
                var cell = plotCells[i];
                if( cell.cases ) {
                    this.computeAveragesOfCellOnAxis( cell, xAxisView, iconPositions, view );
                    this.computeAveragesOfCellOnAxis( cell, yAxisView, iconPositions, view );
                }
            }

            this.cleanUpCalcAdornments( view );
            // show+update the button position to the selected adornment if it exists, else hide button
            this.selectAdornment( this.selectedAdornment );
        }

        computeOneAverageAndLine( cellValues, cell, axisView, attrIndex, view ) {
            // compute the mean and standard deviation for the collected attribute values in the cell,
            // and if 1+ numeric values, plot the lines on the axis
            let meanValue = this._fixPrecision( Dyn.mathUtil.meanOfNumericArray( cellValues )),
                stDevValue = this._fixPrecision( Dyn.mathUtil.stDevOfNumericArray( cellValues ));

            // NOTE: unlike other averages/spreads, we make one set of lines per each of 1SD, 2SD, 3SD,
            // e.g if this.numStDevs===2, make 1SD region + 2SD region
            if( isFinite( meanValue ) && isFinite( stDevValue ) ) {
                for( let i = 1; i <= this.numStDevs; i++ ) { // for each of 1-3 SDs
                    let linePos = [ meanValue - i * stDevValue, meanValue + i * stDevValue ],
                        avgData = this._getAverageOrSpreadModel( view )
                            .setAsSpread( stDevValue, linePos )
                            .setExtraLine( (i === 1) ? meanValue : Math.NaN ) // 1st SD also shows a dashed mean line
                            .setToolTipParameters( view, { mean: meanValue, stDev: stDevValue, numSDs: i } );
                    if( i > 1 ) {   // if computing 2nd or 3rd SD line
                        avgData.measureValue = null; // don't add another measure in Summary Table, redundant with 1SD
                    }
                    this.addLineAdornment( avgData, cell, axisView, attrIndex, view );
                }
            }
        }

        /**
         * {OVERRIDE} to show both St.Dev and Mean
         * @param tipData {*}
         * @param attribute {Dyn.Attribute}
         * @returns { tip:string, label:string}
         */
        getToolTipString( tipData, attribute ) {
            // Show the +/-
            let precision = this.getPrecision( attribute );

            tipData.meanValue = attribute.axisValueToString( tipData.mean, precision );
            tipData.stDevValue = attribute.axisValueToStringRelativeUnits( tipData.stDev, precision );
            let tip = Dyn.loc( "{{numSDs}} {{nameOfAverage}} of {{attributeName}} = {{meanValue}}±({{numSDs}}×{{stDevValue}})", tipData );
            return {
                tip: tip,
                label: tipData.multiAttrTips ? tip :
                    Dyn.loc( "{{numSDs}} {{nameOfAverage}} = {{meanValue}}±({{numSDs}}×{{stDevValue}})", tipData ),
            };
        };

        /** OVERRIDE: we also make one St. Dev. set for each of 1SD, 2SD, 3SD */
        hasDifferentElements( xAxisView, yAxisView ) {
            let newCount = [
                    this.plotShaper.bins.xBins.length,
                    this.plotShaper.bins.yBins.length,
                    xAxisView.allAttributes.countActive(),
                    yAxisView.allAttributes.countActiveIncludingDualAxis(),
                    this.numStDevs
                ].toString(),
                isDifferent = (newCount !== this.binAndAttributeCount); // compare arrays as strings
            this.binAndAttributeCount = newCount; // save element count for next time
            return isDifferent;
        }

        setViewMode( mode ) {
            // NOTE: buttons may be null, that is OK
            let showControls = mode.showControls(),
                myAdornments = this.getAdornmentsArray(),
                buttonElement = myAdornments[0];

            Dyn.plotUtil.showOrHideElement( buttonElement, showControls );
        }

        setDoubleClickHandler( element ) {
            if( !Dyn.preferences.isViewOnly() ) {
                var plotElement = this;
                element.dblclick( function( event ) {
                    plotElement.showStdDevPicker( event );
                } );
            }
        };

        /**
         * Make a "..." button for the currently selected St. Deviation line.
         * Caller must position the selector.
         * @returns {*} -- Raphael color selector element
         */
        makeMenuButton() {

            function buttonClick( event ) {
                // handler to pick number of st.devs
                // called by click or keydown event
                // this==path element
                setTimeout( () => { // delayed call is required to show when e.type==='keykdown', reason unknown
                    plotElement.showStdDevPicker( event );
                }, 100 );
            }

            var plotElement = this,
                buttonSet = Dyn.plotUtil.makeContextMenuButton(
                    this.plotShaper.adornments.paper,
                    buttonClick,
                    {}, // no keyboard handlers other than Enter=click
                    Dyn.loc( "Number of Standard Deviations" ),
                    Dyn.plotUtil.stdSettingsButtonId );
            return buttonSet;
        }

        /**
         * Move the 3DotButton next to the given St.Dev. region.
         * @param lineSet -- Rafael element set for the region, created by AddLineAdornment()
         */
        position3DotButton( buttonElement, lineSet ) {
            const xAxisOffset = [ 10, 10 ],
                yAxisOffset = [ -10, -10 ];
            let regionRectElement = lineSet.getRegionRectElement(),
                regionRect = regionRectElement.data( "regionRect" ),
                offset = regionRectElement.data( "isOnXAxis" ) ? xAxisOffset : yAxisOffset,
                x = regionRect.x + regionRect.width + offset[0],
                y = regionRect.y + offset[1];
            buttonElement.transform( "t" + x + "," + y );
            Dyn.plotUtil.showOrHideElement( buttonElement, !Dyn.preferences.isViewOnly() );
        }

        updateNumberOfStDeviations( n ) {
            if( n >= 1 ) { // ignore if undefined, happens when user presses Esc
                this.numStDevs = n;
                Dyn.mediator.publish( "plot:updateAdornment", {} ); // to update plot to match
            }
        };

        /**
         * Select an adornment.
         * @param adornmentIndex
         * @param [data] - if defined, the IDs of the object to select
         */
        selectAdornment( adornmentIndex, data ) {
            let oldSelection = this.selectedLineIndex,
                myAdornments = this.getAdornmentsArray(),
                buttonElement = myAdornments[0];
            if( buttonElement && (adornmentIndex >= 1 && adornmentIndex < myAdornments.length) ) {
                this.selectedAdornment = adornmentIndex;
                Dyn.assert( (this.selectedAdornment === -1) || (this.selectedAdornment >= 1), "button element should not be selectable" );
                this.position3DotButton( myAdornments[0], myAdornments[adornmentIndex] );

                this.plotShaper.clearSelectedAdornments( this ); // clear other adornment selections
            } else {
                this.clearSelectedAdornments();
            }
            return (oldSelection !== this.selectedLineIndex);
        }

        /**
         * {OVERRIDE} Deselect any selected adornments in this plot element.
         */
        clearSelectedAdornments() {
            let oldSelection = this.selectedLineIndex,
                myAdornments = this.getAdornmentsArray(),
                buttonElement = myAdornments[0];

            // hide adornments that are shown during selection
            if( buttonElement && Dyn.plotUtil.isElementVisible( buttonElement ) ) {
                buttonElement.hide();
            }
            this.selectedAdornment = -1;
            return (oldSelection !== this.selectedLineIndex);
        }

        /** Optional method for StDeviation. The StDeviation extra line path is the mean line,
         * separated from the other lines because the style is different (see _makeExtraLineElement()) */
        _makeExtraLinePathString( axisView, avgRect, axisValue ) {
            return this._makeLinePathString( axisView, avgRect, axisValue );
        }

        /** Optional method for StDeviation. Make the mean line with a dashed line style */
        _makeExtraLineElement( view, extraLinePath, strokeWidth ) {
            return this._makeDashedMeanLineElement( view, extraLinePath, strokeWidth );
        }

        /**
         * showStdDevPicker
         * @param {*} event
         */
        showStdDevPicker( event ) {
            let xy = Dyn.plotUtil.clickOrKeyboardEventToViewportXY( event );
            ContextMenuUtil.show( {x:xy.x, y:xy.y}, { noOfStdDev: this.numStDevs }, ( noOfStdDev ) => {
                // handle change to St.Dev. context menu options, or close of menu
                if( Number.isFinite( noOfStdDev ) ) {
                    var plotElement = Dyn.plotView.plotShaper.findElement( Dyn.EPlotControl.kControlsStDeviation );
                    plotElement.updateNumberOfStDeviations( noOfStdDev );
                }
            }, Dyn.plotUtil.stdSettingsButtonId, Dyn );
        }

    } // class StDeviationElement

    Dyn.StDeviationElement = StDeviationElement;
    Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsStDeviation, Dyn.StDeviationElement );

//-----------------------------------------------------------------------------------

    /**
     * Dyn.VarianceElement()
     */
    class VarianceElement extends SpreadElement {

        constructor( Dyn ) {
            super( Dyn,
                Dyn.EPlotPriority.kPriorityVariance,
                Dyn.EPlotControl.kControlsVariance );
        }

        get kUndoAction() { return 'Variance'; }

        get kStatName()
        {
            return {
                notSampling: "Variance",
                viewSample: "Sample Variance (s²)",
                viewPopulation: "Population Variance (𝜎²)",
                viewDistribution: "Variance of Distribution",
                nameOfDistrAttr: "Sample Variances"
            };
        }

        /** Update adornments (specific to this element) to show +/-1 standard deviation lines on the plot area */
        calcAdornments( ioAdornArray, iconPositions, xAxisView, yAxisView, noAnimation ) {
            var plotCells = this.plotShaper.bins.cells,
                view = new Dyn.AverageViewUpdater( this, ioAdornArray, xAxisView, yAxisView, noAnimation );
            view.nameOfAverage = Dyn.loc(this.kStatName[this.getSamplingModeKey()]);
            view.nameOfSampleAttr = Dyn.loc( this.kStatName.viewSample );
            view.nameOfDistrAttr = Dyn.loc(this.kStatName.nameOfDistrAttr);
            view.setColor( Dyn.plotUtil.varianceColor );

            // compute the Variance of any numeric attributes on X and Y axis, for each cell created by the categorical axes.
            for ( let i=0, j=plotCells.length; i<j; i++ ){
                var cell = plotCells[i];
                if( cell.cases ){
                    this.computeAveragesOfCellOnAxis( cell, xAxisView, iconPositions, view );
                    this.computeAveragesOfCellOnAxis( cell, yAxisView, iconPositions, view );
                }
            }
            this.cleanUpCalcAdornments( view );
        };

        computeOneAverageAndLine( cellValues, cell, axisView, attrIndex, view ) {
            // compute the mean and standard deviation for the collected attribute values in the cell,
            // and if 1+ numeric values, plot the lines on the axis
            var meanValue = this._fixPrecision( Dyn.mathUtil.meanOfNumericArray( cellValues )),
                varianceValue = this._fixPrecision( Dyn.mathUtil.varianceOfNumericArray( cellValues ));

            if( isFinite( meanValue ) && isFinite(varianceValue)) {
                let upperValue = meanValue + varianceValue,
                    lowerValue = meanValue - varianceValue,
                    avgData = this._getAverageOrSpreadModel( view )
                        .setAsSpread( varianceValue, [lowerValue, /*meanValue,*/ upperValue ] )
                        .setExtraLine( meanValue ) // add dashed mean line, created by _makeExtraLineElement()
                        .setToolTipParameters( view, { mean:meanValue, spread:varianceValue });
                this.addLineAdornment( avgData, cell, axisView, attrIndex, view );
            }
        };

        /**
         * {OVERRIDE} to show both Variance and Mean
         * @param tipData {*}
         * @param attribute {Dyn.Attribute}
         * @returns { tip:string, label:string}
         */
        getToolTipString( tipData, attribute ) {
            return this._getMeanPlusMinusSpreadToolTip( tipData, attribute );
        };

        /** Optional method. Return the path of the mean line, for _makeExtraLineElement() */
        _makeExtraLinePathString( axisView, avgRect, axisValue ) {
            return this._makeLinePathString( axisView, avgRect, axisValue );
        };

        /** Optional method. Along with Variance line, make a mean line with a dashed line style */
        _makeExtraLineElement( view, extraLinePath, strokeWidth ) {
            return this._makeDashedMeanLineElement( view, extraLinePath, strokeWidth );
        };

    } // class VarianceElement
    Dyn.VarianceElement = VarianceElement;
    Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsVariance, Dyn.VarianceElement );

    //-----------------------------------------------------------------------------------


    /**
     * Dyn.InterQuartRangeElement()
     */
    class InterQuartRangeElement extends SpreadElement {

        constructor( Dyn ) {
            super( Dyn,
                Dyn.EPlotPriority.kPriorityInterQuartRange,
                Dyn.EPlotControl.kControlsInterQuartRange );
        };

        get kUndoAction() { return 'Interquartile Range'; }

        get kStatName() { return {
            notSampling: "Interquartile Range",
            viewSample: "Sample Interquartile Range",
            viewPopulation: "Population Interquartile Range",
            viewDistribution: "Interquartile Range of Distribution",
            nameOfDistrAttr: "Sample IQRs"
        }}

        /** Update adornments (specific to this element) to show +/-1 standard deviation lines on the plot area */
        calcAdornments( ioAdornArray, iconPositions, xAxisView, yAxisView, noAnimation ) {
            var plotCells = this.plotShaper.bins.cells,
                view = new Dyn.AverageViewUpdater( this, ioAdornArray, xAxisView, yAxisView, noAnimation );
            view.nameOfAverage = Dyn.loc(this.kStatName[this.getSamplingModeKey()]);
            view.nameOfSampleAttr = Dyn.loc( this.kStatName.viewSample );
            view.nameOfDistrAttr = Dyn.loc(this.kStatName.nameOfDistrAttr);
            view.setColor( Dyn.plotUtil.iqrColor );

            // compute the IQR of any numeric attributes on X and Y axis, for each cell created by the categorical axes.
            for ( let i=0, j=plotCells.length; i<j; i++ ){
                var cell = plotCells[i];
                if( cell.cases ){
                    this.computeAveragesOfCellOnAxis( cell, xAxisView, iconPositions, view );
                    this.computeAveragesOfCellOnAxis( cell, yAxisView, iconPositions, view );
                }
            }
            this.cleanUpCalcAdornments( view );
        }

        computeOneAverageAndLine( cellValues, cell, axisView, attrIndex, view ) {
            // compute the median and IQR for the collected attribute values in the cell,
            // and if 1+ numeric values, plot the lines on the axis
            let quartiles = Dyn.mathUtil.quartilesOfNumericArray( cellValues );
            quartiles.IQR = this._fixPrecision( quartiles.IQR ); // #1664 prepare for rounding
            quartiles.q1  = this._fixPrecision( quartiles.q1 ); // #1664 prepare for rounding
            quartiles.q3  = this._fixPrecision( quartiles.q3 ); // #1664 prepare for rounding

            if ( isFinite( quartiles.IQR ) && isFinite(quartiles.q1) && isFinite(quartiles.q3)) {
                let avgData = this._getAverageOrSpreadModel( view )
                    .setAsSpread( quartiles.IQR, [ quartiles.q1, quartiles.q3 ] )
                    .setToolTipParameters( view, quartiles );
                this.addLineAdornment( avgData, cell, axisView, attrIndex, view );
            }
        }

        /**
         * {OVERRIDE} to show IQR plus Q1 and Q3 axis locations.
         * @param tipData {*}
         * @param attribute {Dyn.Attribute}
         * @returns { tip:string, label:string}
         */
        getToolTipString( tipData, attribute ) {
            // Show Quartile1, Quartile3, and IRQ
            let precision = this.getPrecision( attribute );

            tipData.Q1Str = attribute.axisValueToString( tipData.q1, precision);
            tipData.Q3Str = attribute.axisValueToString( tipData.q3, precision);
            tipData.IRQStr = attribute.axisValueToStringRelativeUnits( tipData.IQR, precision );
            let tip = Dyn.loc( "{{nameOfAverage}} of {{attributeName}} : ({{Q3Str}} - {{Q1Str}}) = {{IRQStr}}", tipData );
            return {
                tip: tip,
                label: tipData.multiAttrTips ? tip :
                    Dyn.loc( "{{nameOfAverage}} : ({{Q3Str}} - {{Q1Str}}) = {{IRQStr}}", tipData ),
            };
        }

    } // class InterQuartRangeElement
    Dyn.InterQuartRangeElement = InterQuartRangeElement;
    Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsInterQuartRange, Dyn.InterQuartRangeElement );

//-----------------------------------------------------------------------------------

    /**
     * Dyn.StdErrorElement() - Standard Error is a measure of data spread.
     */
    class StdErrorElement extends SpreadElement {

        constructor( Dyn ) {
            super( Dyn,
                Dyn.EPlotPriority.kPriorityStdError,
                Dyn.EPlotControl.kControlsStdError );
       }

        get kUndoAction() {
            return 'Std. Error';
        }

        get kStatName() {
            return {
                notSampling: "Std. Error",
                viewSample: "Std. Error",
                //viewPopulation: "Population Std. Error",
                //viewDistribution: "Std. Error of Distribution",
                nameOfDistrAttr: "Std. Error"
            };
        }

        /** Update adornments (specific to this element) to show +/-1 standard deviation lines on the plot area */
        calcAdornments( ioAdornArray, iconPositions, xAxisView, yAxisView, noAnimation ) {
            var plotCells = this.plotShaper.bins.cells,
                view = new Dyn.AverageViewUpdater( this, ioAdornArray, xAxisView, yAxisView, noAnimation ),
                isViewingSampleOrNotSampling = (!this.isInSamplingMode()) || this.isViewingSample();
            view.nameOfAverage = Dyn.loc( this.kStatName.notSampling );
            view.nameOfSampleAttr = Dyn.loc( this.kStatName.viewSample );
            view.nameOfDistrAttr = Dyn.loc( this.kStatName.nameOfDistrAttr );
            view.setColor( Dyn.plotUtil.stdErrColor );

            // compute the SE of any numeric attributes on X and Y axis, for each cell created by the categorical axes.
            // and suppress SE display if viewing Sample Population or Sample Distributions.
            for( let i = 0, j = plotCells.length; i < j; i++ ) {
                var cell = plotCells[i];
                if( cell.cases && isViewingSampleOrNotSampling ) {
                    this.computeAveragesOfCellOnAxis( cell, xAxisView, iconPositions, view );
                    this.computeAveragesOfCellOnAxis( cell, yAxisView, iconPositions, view );
                }
            }
            this.cleanUpCalcAdornments( view );
        };

        computeOneAverageAndLine( cellValues, cell, axisView, attrIndex, view ) {
            // compute the mean and SE for the collected attribute values in the cell,
            // and if 1+ numeric values, plot the lines on the axis
            var meanValue = this._fixPrecision( Dyn.mathUtil.meanOfNumericArray( cellValues )),
                stdErrorValue = this._fixPrecision( Dyn.mathUtil.stdErrorOfNumericArray( cellValues ));

            if( isFinite( meanValue ) && isFinite( stdErrorValue ) ) {
                let upperValue = meanValue + stdErrorValue,
                    lowerValue = meanValue - stdErrorValue,
                    avgData = this._getAverageOrSpreadModel( view )
                        .setAsSpread( stdErrorValue, [ lowerValue /*, meanValue,*/, upperValue ] )
                        .setExtraLine( meanValue ) // add custom notched bar around mean; created by _makeExtraLinePathString()
                        .setToolTipParameters( view, { mean: meanValue, spread: stdErrorValue } );
                this.addLineAdornment( avgData, cell, axisView, attrIndex, view );
            }
        };

        /**
         * {OVERRIDE} to show both Standard Error and Mean
         * @param tipData {*}
         * @param attribute {Dyn.Attribute}
         * @returns { tip:string, label:string}
         */
        getToolTipString( tipData, attribute ) {
            return this._getMeanPlusMinusSpreadToolTip( tipData, attribute );
        };

        /** {OVERRIDE} to make average rectangle for Standard Error short, not full cell height;
         * instead just cover the error bar lines */
        _makeRectOfAveragLineOrSpread( axisView, cell, orderedAxisValues ) {
            let rect = super._makeRectOfAveragLineOrSpread( axisView, cell, orderedAxisValues ),
                barHalfLength = Dyn.plotUtil.stdErrorCrossBarSize * Dyn.preferences.getFontSize() / 2;
            if( axisView.isHorizontal ) {
                let rectCenterYPos = cell.height * (1 - Dyn.plotUtil.stdErrorCellPosition); // 0.x of cell height from x-axis
                rect.y = cell.y + Math.max( rectCenterYPos - barHalfLength,
                    0 ); // keep within cell
                rect.height = Math.min( 2 * barHalfLength,
                    cell.height - (rect.y - cell.y) ); // keep within cell
            } else { // Y or Y2 axis, see axisView.id
                let rectCenterXPos = cell.width * Dyn.plotUtil.stdErrorCellPosition; // 0.x of cell height from x-axis
                rect.x = cell.x + Math.max( rectCenterXPos - barHalfLength, // 0.x of cell width from y-axis
                    0 ); // keep within cell
                rect.width = Math.min( 2 * barHalfLength,
                    cell.width - (rect.x - cell.x) ); // keep within cell
            }
            return rect;
        };

        /** {OVERRIDE} to hide shaded region rectangle for Standard Error. */
        _makeRegionRectElement( oldElement, view, avgRect ) {
            let regionRectElement = super._makeRegionRectElement( oldElement, view, avgRect );
            regionRectElement.hide();
            return regionRectElement;
        };

        /**
         * Optional method for Standard Error, to create the notched bar around the mean point, with the ends
         * of the bar at mean +/- 1 SE.
         * Note that the short lines at the ends of the bars are determined by _makeRectOfAveragLineOrSpread()
         * See also _makeExtraLineElement())
         * @param axisView
         * @param avgRect
         * @param axisValue
         * @returns {string}
         * @private
         */
        _makeExtraLinePathString( axisView, avgRect, axisValue ) {
            let bw = Dyn.plotUtil.stdErrorBarWidth * Dyn.preferences.getFontSize(), // bar width
                nw = Math.min( bw, 0.5 * avgRect.widthOrHeight( axisView.isHorizontal ) ), // notch width
                bndiff = bw - nw, // difference between bar width and notch width, if not the same
                pathString;

            function m( x, y ) {
                return 'M' + x + ',' + y;
            } // move to
            function l( x, y ) {
                return 'L' + x + ',' + y;
            } // line to

            if( axisView.isHorizontal ) {
                // parallel to X-axis
                let x1 = avgRect.left(),
                    x2 = avgRect.right(),
                    cx = axisView.dataToPixel( axisValue ),
                    y = avgRect.centerY();
                pathString = m( x1, y + bw ) + l( cx - nw, y + bw ) + l( cx, y + bndiff ) // left upper line and notch
                    + l( cx + nw, y + bw ) + l( x2, y + bw ) // right upper line and notch
                    + l( x2, y - bw ) + l( cx + nw, y - bw ) + l( cx, y - bndiff ) // right lower line and notch
                    + l( cx - nw, y - bw ) + l( x1, y - bw ) + 'z'; // left lower line and notch
            } else {
                // parallel to Y- or Y2-axis, see axisView.id
                let x = avgRect.centerX(),
                    y1 = avgRect.top(),
                    y2 = avgRect.bottom(),
                    cy = axisView.dataToPixel( axisValue );
                pathString = m( x + bw, y1 ) + l( x + bw, cy - nw ) + l( x + bndiff, cy ) // top right line and notch
                    + l( x + bw, cy + nw ) + l( x + bw, y2 ) // bottom right line and notch
                    + l( x - bw, y2 ) + l( x - bw, cy + nw ) + l( x - bndiff, cy ) // top left line and notch
                    + l( x - bw, cy - nw ) + l( x - bw, y1 ) + 'z'; // bottom left line and notch
            }
            return pathString;
        };

        /** Optional method for Standard Error, to add a line connecting the S.E. bars, parallel to axis.
         *  See _makeExtraLinePathString for path definition. */
        _makeExtraLineElement( view, extraLinePath, strokeWidth ) {
            let extraLineElement = view.paper.path( extraLinePath )
                .attr( {
                    stroke: view.lineColor, 'stroke-width': strokeWidth,
                    fill: view.lineColor,
                    opacity: 0, 'clip-rect': view.clipRectString
                } )
                .animate( { opacity: 1 }, view.animationDelay, view.animationEasing );
            return extraLineElement;
        };
    } // class StdErrorElement

    Dyn.StdErrorElement = StdErrorElement;
    Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsStdError, Dyn.StdErrorElement );

    //-----------------------------------------------------------------------------------


    /**
     * Dyn.MeanAvDevElement() - computes Mean Average Deviation, a measure of data spread.
     */
    class MeanAvDevElement extends SpreadElement {

        constructor( Dyn ) {
            super( Dyn,
                Dyn.EPlotPriority.kPriorityMeanAvDev,
                Dyn.EPlotControl.kControlsMeanAvDev );
        }

        get kUndoAction() {
            return 'Mean Average Deviation';
        }

        /** Update adornments (specific to this element) to show a mean average deviation area on the plot area */
        calcAdornments( ioAdornArray, iconPositions, xAxisView, yAxisView, noAnimation ) {
            let plotCells = this.plotShaper.bins.cells,
                view = new Dyn.AverageViewUpdater( this, ioAdornArray, xAxisView, yAxisView, noAnimation );
            view.setColor( Dyn.plotUtil.meanAvDevColor );
            view.nameOfAverage = Dyn.loc( "Mean Average Deviation" );
            view.wantSampleMeasure = false; // Don't show Mode in Sample Statistics table

            // compute the mode of any numeric attributes on X and Y axis, for each cell created by the categorical axes.
            for( let i = 0, j = plotCells.length; i < j; i++ ) {
                var cell = plotCells[i];
                if( cell.cases ) {
                    this.computeAveragesOfCellOnAxis( cell, xAxisView, iconPositions, view );
                    this.computeAveragesOfCellOnAxis( cell, yAxisView, iconPositions, view );
                }
            }
            this.cleanUpCalcAdornments( view );
        };

        computeOneAverageAndLine( cellValues, cell, axisView, attrIndex, view ) {
            let meanValue = this._fixPrecision( Dyn.mathUtil.meanOfNumericArray( cellValues )),
                meanAvDevValue = this._fixPrecision( Dyn.mathUtil.meanAvDevOfNumericArray( cellValues, meanValue ));

            if( isFinite( meanValue ) && isFinite( meanAvDevValue ) ) {
                let upperValue = meanValue + meanAvDevValue,
                    lowerValue = meanValue - meanAvDevValue,
                    avgData = this._getAverageOrSpreadModel( view )
                        .setAsSpread( meanAvDevValue, [ lowerValue, /*meanValue,*/ upperValue ] )
                        .setExtraLine( meanValue ) // add dashed mean line, created by _makeExtraLineElement()
                        .setToolTipParameters( view, { mean: meanValue, spread: meanAvDevValue } );
                this.addLineAdornment( avgData, cell, axisView, attrIndex, view );
            }
        };

        /**
         * {OVERRIDE} to show both MeanAvDev and Mean
         * @param tipData {*}
         * @param attribute {Dyn.Attribute}
         * @returns { tip:string, label:string}
         */
        getToolTipString( tipData, attribute ) {
            return this._getMeanPlusMinusSpreadToolTip( tipData, attribute );
        };

        /** Optional method. Return the path of the mean line, for _makeExtraLineElement() */
        _makeExtraLinePathString( axisView, avgRect, axisValue ) {
            return this._makeLinePathString( axisView, avgRect, axisValue );
        };

        /** Optional method. Along with Variance line, make a mean line with a dashed line style */
        _makeExtraLineElement( view, extraLinePath, strokeWidth ) {
            return this._makeDashedMeanLineElement( view, extraLinePath, strokeWidth );
        };

    } // class MeanAvDevElement

    Dyn.MeanAvDevElement = MeanAvDevElement;
    Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsMeanAvDev, Dyn.MeanAvDevElement );
    //Dyn.MeanAvDevElement.inheritsFrom( Dyn.averageElementBase );

    //-----------------------------------------------------------------------------------


//-----------------------------------------------------------------------------------

    /**
     * Dyn.PopulationStDeviationElement()
     */
    class PopulationStDeviationElement extends SpreadElement {

        constructor( Dyn ) {
            super( Dyn,
                Dyn.EPlotPriority.kPriorityPopulationStDeviation,
                Dyn.EPlotControl.kControlsPopulationStDeviation );
        }

        get kUndoAction() {
            return 'Population St. Deviation ( 𝜎 )';
        }

        /** adornments (specific to this element) draw a StDeviation line on the plot area */
        calcAdornments( ioAdornArray, iconPositions, xAxisView, yAxisView, noAnimation ) {
            var //plotCells = this.plotShaper.bins.cells,
                populationArray = this.plotShaper.dataSet.getPopulationCaseArray(),
                view = new Dyn.AverageViewUpdater( this, ioAdornArray, xAxisView, yAxisView, noAnimation, (populationArray !== null) );
            view.nameOfAverage = Dyn.loc( "Population Std. Deviation (𝜎)" );
            view.isPopulationStat = true;
            view.setColor( Dyn.plotUtil.stDevColor );

            // compute the population Std. Deviation of any numeric attributes on X and Y axis
            if( populationArray.length > 0 && !this.wantPopulationStatsHidden() ) {
                this.computeAveragesOfPopulationOnAxis( populationArray, xAxisView, iconPositions, view );
                this.computeAveragesOfPopulationOnAxis( populationArray, yAxisView, iconPositions, view );
            }
            this.cleanUpCalcAdornments( view );
        };

        computeOneAverageAndLine( values, axisView, attrIndex, view ) {
            // compute the mean and standard deviation for the collected attribute values in the cell,
            // and if 1+ numeric values, plot the lines on the axis
            let meanValue = this._fixPrecision( Dyn.mathUtil.meanOfNumericArray( values )),
                stDevValue = this._fixPrecision( Dyn.mathUtil.stDevOfNumericArray( values, true /* get Population S.D. (n), not Sample S.D. (n-1) */ ));

            if( isFinite( meanValue ) && isFinite( stDevValue ) ) {
                let upperValue = meanValue + stDevValue,
                    lowerValue = meanValue - stDevValue,
                    avgData = this._getAverageOrSpreadModel( view )
                        .setAsSpread( stDevValue, [ lowerValue, /*meanValue,*/ upperValue ] )
                        .setExtraLine( meanValue ) // add dashed mean line, created by _makeExtraLineElement()
                        .setToolTipParameters( view, { mean: meanValue, spread: stDevValue } ),
                    cell = this.getPopulationCell();
                this.addLineAdornment( avgData, cell, axisView, attrIndex, view );
            }
        }

        /**
         * {OVERRIDE} to show both Population St.Dev and Mean
         * @param tipData {*}
         * @param attribute {Dyn.Attribute}
         * @returns { tip:string, label:string}
         */
        getToolTipString = function( tipData, attribute ) {
            return this._getMeanPlusMinusSpreadToolTip( tipData, attribute );
        }

        /** Optional method. Return the path of the mean line, for _makeExtraLineElement() */
        _makeExtraLinePathString( axisView, avgRect, axisValue ) {
            return this._makeLinePathString( axisView, avgRect, axisValue );
        }

        /** Optional method. Along with Pop.St.Dev. line, make a mean line with a dashed line style */
        _makeExtraLineElement( view, extraLinePath, strokeWidth ) {
            return this._makeDashedMeanLineElement( view, extraLinePath, strokeWidth );
        }
    } // class PopulationStDeviationElement

    Dyn.PopulationStDeviationElement = PopulationStDeviationElement;
    Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsPopulationStDeviation, Dyn.PopulationStDeviationElement );

//-----------------------------------------------------------------------------------

    /**
     * Dyn.PopulationVarianceElement()
     */
    class PopulationVarianceElement extends SpreadElement {
        constructor( Dyn ) {
            super( Dyn,
                Dyn.EPlotPriority.kPriorityPopulationVariance,
                Dyn.EPlotControl.kControlsPopulationVariance );
        }

        get kUndoAction() {
            return 'Population Variance ( 𝜎² )';
        }

        /** adornments (specific to this element) draw a StDeviation line on the plot area */
        calcAdornments( ioAdornArray, iconPositions, xAxisView, yAxisView, noAnimation ) {
            var //plotCells = this.plotShaper.bins.cells,
                populationArray = this.plotShaper.dataSet.getPopulationCaseArray(),
                view = new Dyn.AverageViewUpdater( this, ioAdornArray, xAxisView, yAxisView, noAnimation, (populationArray !== null) );
            view.nameOfAverage = Dyn.loc( "Population Variance (𝜎²)" );
            view.isPopulationStat = true;
            view.setColor( Dyn.plotUtil.varianceColor );

            // compute the population Std. Deviation of any numeric attributes on X and Y axis
            if( populationArray.length > 0 && !this.wantPopulationStatsHidden() ) {
                this.computeAveragesOfPopulationOnAxis( populationArray, xAxisView, iconPositions, view );
                this.computeAveragesOfPopulationOnAxis( populationArray, yAxisView, iconPositions, view );
            }
            this.cleanUpCalcAdornments( view );
        };

        computeOneAverageAndLine( values, axisView, attrIndex, view ) {
            // compute the mean and standard deviation for the collected attribute values in the cell,
            // and if 1+ numeric values, plot the lines on the axis
            let meanValue = this._fixPrecision( Dyn.mathUtil.meanOfNumericArray( values )),
                varianceValue = this._fixPrecision( Dyn.mathUtil.varianceOfNumericArray( values,
                    true /* get Population Variance (n), not Sample Variance (n-1) */ ));

            if( isFinite( meanValue ) && isFinite( varianceValue ) ) {
                let upperValue = meanValue + varianceValue,
                    lowerValue = meanValue - varianceValue,
                    avgData = this._getAverageOrSpreadModel( view )
                        .setAsSpread( varianceValue, [ lowerValue, /*meanValue,*/ upperValue ] )
                        .setExtraLine( meanValue ) // add dashed mean line, created by _makeExtraLineElement()
                        .setToolTipParameters( view, { mean: meanValue, spread: varianceValue } ),
                    cell = this.getPopulationCell();
                this.addLineAdornment( avgData, cell, axisView, attrIndex, view );
            }
        };

        /**
         * {OVERRIDE} to show both Population Variance and Mean
         * @param tipData {*}
         * @param attribute {Dyn.Attribute}
         * @returns { tip:string, label:string}
         */
        getToolTipString( tipData, attribute ) {
            return this._getMeanPlusMinusSpreadToolTip( tipData, attribute );
        };

        /** Optional method. Return the path of the mean line, for _makeExtraLineElement() */
        _makeExtraLinePathString( axisView, avgRect, axisValue ) {
            return this._makeLinePathString( axisView, avgRect, axisValue );
        };

        /** Optional method. Along with Pop.Variance. lines, make a mean line with a dashed line style */
        _makeExtraLineElement( view, extraLinePath, strokeWidth ) {
            return this._makeDashedMeanLineElement( view, extraLinePath, strokeWidth );
        };
    } // class PopulationVarianceElement

    Dyn.PopulationVarianceElement = PopulationVarianceElement;
    Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsPopulationVariance, Dyn.PopulationVarianceElement );

//-----------------------------------------------------------------------------------

    /**
     * Dyn.PopulationInterQuartRangeElement()
     */
    class PopulationInterQuartRangeElement extends SpreadElement {

        constructor( Dyn ) {
            super( Dyn,
                Dyn.EPlotPriority.kPriorityPopulationInterQuartRange,
                Dyn.EPlotControl.kControlsPopulationInterQuartRange );
        }

        get kUndoAction() {
            return 'Population Interquartile Range';
        }

        /** adornments (specific to this element) draw a StDeviation line on the plot area */
        calcAdornments( ioAdornArray, iconPositions, xAxisView, yAxisView, noAnimation ) {
            var //plotCells = this.plotShaper.bins.cells,
                populationArray = this.plotShaper.dataSet.getPopulationCaseArray(),
                view = new Dyn.AverageViewUpdater( this, ioAdornArray, xAxisView, yAxisView, noAnimation, (populationArray !== null) );
            view.nameOfAverage = Dyn.loc( "Population Interquartile Range" );
            view.isPopulationStat = true;
            view.setColor( Dyn.plotUtil.iqrColor );

            // compute the population Std. Deviation of any numeric attributes on X and Y axis
            if( populationArray.length > 0 && !this.wantPopulationStatsHidden() ) {
                this.computeAveragesOfPopulationOnAxis( populationArray, xAxisView, iconPositions, view );
                this.computeAveragesOfPopulationOnAxis( populationArray, yAxisView, iconPositions, view );
            }
            this.cleanUpCalcAdornments( view );
        };

        computeOneAverageAndLine( values, axisView, attrIndex, view ) {
            // compute the median and IQR for the collected attribute values in the cell,
            // and if 1+ numeric values, plot the lines on the axis
            let quartiles = Dyn.mathUtil.quartilesOfNumericArray( values );
            quartiles.IQR = this._fixPrecision( quartiles.IQR ); // #1664 prepare for rounding
            quartiles.q1  = this._fixPrecision( quartiles.q1 ); // #1664 prepare for rounding
            quartiles.q3  = this._fixPrecision( quartiles.q3 ); // #1664 prepare for rounding

            if( isFinite( quartiles.IQR ) && isFinite( quartiles.q1 ) && isFinite( quartiles.q3 ) ) {
                let avgData = this._getAverageOrSpreadModel( view )
                        .setAsSpread( quartiles.IQR, [ quartiles.q1, quartiles.q3 ] )
                        .setToolTipParameters( view, quartiles ),
                    cell = this.getPopulationCell();
                this.addLineAdornment( avgData, cell, axisView, attrIndex, view );
            }
        };

        /**
         * {OVERRIDE} to show IQR with first and 3rd quartile axis values
         * @param tipData {*}
         * @param attribute {Dyn.Attribute}
         * @returns { tip:string, label:string}
         */
        getToolTipString( tipData, attribute ) {
            // Show Quartile1, Quartile3, and IRQ
            let precision = this.getPrecision( attribute );

            tipData.Q1Str = attribute.axisValueToString( tipData.q1, precision );
            tipData.Q3Str = attribute.axisValueToString( tipData.q3, precision );
            tipData.IRQStr = attribute.axisValueToStringRelativeUnits( tipData.IQR, precision );
            let tip = Dyn.loc( "{{nameOfAverage}} of {{attributeName}} : ({{Q3Str}} - {{Q1Str}}) = {{IRQStr}}", tipData );
            return {
                tip: tip,
                label: tipData.multiAttrTips ? tip :
                    Dyn.loc( "{{nameOfAverage}} : ({{Q3Str}} - {{Q1Str}}) = {{IRQStr}}", tipData ),
            };
        };

    } // class PopulationInterQuartRangeElement

    Dyn.PopulationInterQuartRangeElement = PopulationInterQuartRangeElement;
    Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsPopulationInterQuartRange, Dyn.PopulationInterQuartRangeElement );

} // wrapDynSpreadElement
